import React from 'react';

import { useQuery } from '@apollo/client/react';
import { Table } from '@repo/ui/Table';
import { decodePaging, PAGE_SCHEMA, Pagination } from '@repo/ui/Pagination';
import { z } from 'zod';
import { coerce } from '@repo/util/coerceForTable';
import type { SelectInputOptions } from '@repo/types/Types';
import { getNonEmpty } from '@repo/util/nonEmpty';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Alert, AlertTitle, AlertDescription } from '@repo/ui/Alert';

import GET_ACCOUNT_ALLOWED_ACCESS_IPS_FOR_ACCOUNT_Q from '../gql/getWhitelistForAccount.gql';

import WhitelistForm from './WhitelistForm';
import DeleteIp from './DeleteIp';
import UpdateIpForm from './UpdateIpForm';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';

import WhitelistTableFilter from './WhitelistTableFilter';
import { Form } from '@repo/ui/Form';

import type { Mercury } from '@repo/types/Models';

const SCOPES: SelectInputOptions = [
  { label: 'API', value: 'api' },
  { label: 'Dashboard', value: 'gql' },
  { label: 'Any', value: 'any' },
] as const;

const WHITELIST_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  address: z.string().default(''),
  name: z.string().default(''),
  scope: z
    .enum(getNonEmpty(SCOPES.map((dir) => dir.value)))
    .default('')
    .or(z.literal(''))
    .default(''),
});

const PAGE_SIZE = 10;

const getScopeLabel = (scope: string): string => {
  const scopeMap: Record<string, string> = {
    API: 'API',
    GQL: 'Dashboard',
    ANY: 'Any',
  };
  return scopeMap[scope] || scope;
};

export interface WhitelistTableProps {
  accountId: Mercury.Models.Account['id'];
}

const WhitelistTable = ({ accountId }: WhitelistTableProps) => {
  const user = useUser();

  const canCreate = POLICIES['account_allowed_access_ips:create'](user);
  const canUpdate = POLICIES['account_allowed_access_ips:update'](user);
  const canDelete = POLICIES['account_allowed_access_ips:delete'](user);

  const filter = useZodQuery({
    schema: WHITELIST_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['name', 'address']
    ),
  ];

  const { loading, error, data, refetch } = useQuery(GET_ACCOUNT_ALLOWED_ACCESS_IPS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId, //01J9QGB6MTT3N1JSTJTSAAAQKP
    },
    notifyOnNetworkStatusChange: true,
  });

  const { edges: accountAllowedAccessIpNodes, pageInfo } = data?.allowedAccessIpsForAccount ?? {};

  return (
    <div className="">
      <div className="flex flex-col gap-12">
        <Alert icon="info" variant="default">
          <AlertTitle>Warning</AlertTitle>
          <AlertDescription className="">
            Adding IP addresses to your whitelist will restrict access to your account’s API and
            Dashboard. Only users connecting from the IP addresses listed below will be able to
            access your account. Make sure to add all necessary IPs to avoid unintended lockouts.
          </AlertDescription>
        </Alert>

        <Form {...filter} variant="horizontal" className={'mb-6'}>
          <WhitelistTableFilter SCOPES={SCOPES} queryList={queryList} />
        </Form>
      </div>

      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={accountAllowedAccessIpNodes?.map((ip) => ip.node) ?? []}
        dataType="Account Allowed Access IPs"
        columns={[
          {
            title: 'Address',
            Cell({ entry: { address } }) {
              return <p>{address as string}</p>;
            },
          },
          {
            title: 'Name',
            Cell({ entry: { name } }) {
              return <p>{name}</p>;
            },
          },
          {
            title: 'Scope',
            Cell({ entry: { scope } }) {
              return <p>{getScopeLabel(scope as string)}</p>;
            },
          },
          {
            title: canUpdate ? 'Update' : '',
            width: canUpdate ? 'sm' : 'hidden',
            Cell({ entry: { address, name, scope, id } }) {
              return (
                <UpdateIpForm
                  loading={loading}
                  address={address as string}
                  scope={scope as 'API' | 'GQL' | 'ANY'}
                  name={name as string}
                  id={id as string}
                  refetch={refetch}
                />
              );
            },
          },

          {
            title: canDelete ? 'Delete' : '',
            width: canDelete ? 'sm' : 'hidden',
            Cell({ entry: { id } }) {
              return <DeleteIp loading={loading} ipId={id} refetch={refetch} />;
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}

      {canCreate ? <WhitelistForm accountId={accountId} refetch={refetch} /> : null}
    </div>
  );
};

export default WhitelistTable;
